import UploadFileIcon from '@mui/icons-material/UploadFile';
import MicIcon from '@mui/icons-material/Mic';
import { Button, ButtonGroup, CircularProgress, Stack, Menu, MenuItem, Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";
import { DataGrid } from '@mui/x-data-grid';
import Box from "@mui/material/Box";
import moment from "moment";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import useApiHook from "../hooks/useApiHook";
import FileUploadDialog from "./FileUploadDialog";
import AudioRecorderDialog from "./AudioRecorderDialog";
import { createSearchParams, NavLink, useNavigate } from "react-router-dom";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import IconButton from "@mui/material/IconButton";
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useDeleteFileModal, useDeletePatientModal, useMoveFileToPatientModal, useRenamePatientModal } from "../store/modal-context";
import { useNotification } from "../store/notification-context";
import { useAuth } from "../store/auth-context";
import useAppView from "./platforms/useAppView";
import useDocumentVisibility from "../hooks/useDocumentVisibility";
import ResponsiveDialog from "./_atoms/ResponsiveDialog";
import VisuallyHiddenInput from "./_atoms/inputs/VisuallyHiddenInput";

moment.locale('it');

const ACTION_TYPES = {
    upload: 'upload',
    record: 'record'
};
const renderFileStatus = (value) => {
    if (value === 'waiting') {
        return (
            <Tooltip title="Trascrizione in elaborazione">
                <CircularProgress sx={{ m: "1px" }} size={20} />
            </Tooltip>
        );
    }

    if (value === 'error') {
        return (
            <Tooltip title="Errore durante la trascrizione">
                <ErrorOutlineIcon color="error" />
            </Tooltip>
        );
    }

    if (value === 'uploaded') {
        return (
            <Tooltip title="File caricato, trascrizione non avviata">
                <HourglassTopIcon color="disabled" />
            </Tooltip>
        );
    }

    return (
        <Tooltip title="Trascrizione completata">
            <CheckCircleIcon color="success" />
        </Tooltip>
    );
};

const FileList = ({ patientId = null, scope = null, title = "Tutti i Pazienti/File Trascritti", currentPatientName = null }) => {
    const { response, makeRequest } = useApiHook();
    const moveFileToPatientModal = useMoveFileToPatientModal();
    const deleteFileModal = useDeleteFileModal();
    const renamePatientModal = useRenamePatientModal();
    const deletePatientModal = useDeletePatientModal();
    const { user } = useAuth();
    const { notifyTranscriptionCompleted } = useNotification();
    const { isAppView } = useAppView();
    const isDocumentVisible = useDocumentVisibility();
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [recorderDialogOpen, setRecorderDialogOpen] = useState(false);
    const [emptyAssistantDialogOpen, setEmptyAssistantDialogOpen] = useState(false);
    const [patientOptionsAnchorEl, setPatientOptionsAnchorEl] = useState(null);
    const [activeActionPatient, setActiveActionPatient] = useState(null);
    const [retryActionPatient, setRetryActionPatient] = useState(null);
    const [autoOpenUploadPicker, setAutoOpenUploadPicker] = useState(false);
    const [prefilledUploadFile, setPrefilledUploadFile] = useState(null);
    const [pendingRootFileIdsKey, setPendingRootFileIdsKey] = useState('');
    const [selectedFileIds, setSelectedFileIds] = useState([]);
    const directUploadInputRef = useRef(null);
    const navigate = useNavigate();
    const pollingIntervalRef = useRef(null);
    const previousHasWaitingRef = useRef(false);
    const previousFilesRef = useRef([]);
    const backgroundedDuringPollingRef = useRef(false);
    const isPatientView = Boolean(patientId) && scope !== 'root';
    const isAdminVerificationPending = user?.role === 'admin' && user?.adminSessionActive !== true;
    const targetPatientId = patientId && scope !== 'root' ? patientId : (activeActionPatient?.id ?? retryActionPatient?.id ?? null);
    const targetPatientName = patientId && scope !== 'root' ? currentPatientName : (activeActionPatient?.name ?? retryActionPatient?.name ?? null);

    const getAllFiles = useCallback(() => {
        const requestData = {};

        if (patientId && scope !== 'root') {
            requestData.folderId = patientId;
        }

        if (scope) {
            requestData.scope = scope;
        }

        return makeRequest('/file/getAll', 'get', requestData);
    }, [patientId, makeRequest, scope]);

    const files = useMemo(() => {
        if (!response || !Array.isArray(response)) return [];
        return response.map(({ id, name, folderId, folderName, createdAt, status, duration }) => ({
            id,
            info: {
                name,
                patientId: folderId,
                patientName: folderName
            },
            uploadDate: new Date(createdAt),
            state: status,
            duration: duration || "0:0"
        }));
    }, [response]);
    const selectedFile = useMemo(
        () => files.find((file) => file.id === selectedFileIds[0]) || null,
        [files, selectedFileIds]
    );
    const hasAnalyzableFiles = useMemo(() => files.some((file) => file.state === 'done'), [files]);
    const canAnalyzeSelection = useMemo(() => {
        if (isPatientView) {
            if (!hasAnalyzableFiles) {
                return false;
            }

            return !selectedFile || selectedFile.state === 'done';
        }

        return Boolean(selectedFile && selectedFile.info?.patientId && selectedFile.state === 'done');
    }, [hasAnalyzableFiles, isPatientView, selectedFile]);
    const dashboardActionButtonSx = useMemo(() => ({
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        cursor: 'pointer',
        fontSize: { xs: '0.98rem', sm: '1.05rem' },
        fontWeight: 700,
        minWidth: 'fit-content',
        '& .MuiButton-startIcon': {
            mr: 0.75
        },
        '& .MuiButton-startIcon > *:first-of-type': {
            fontSize: '1.35em'
        },
        '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: 'none'
        }
    }), []);
    const assistantButtonSx = useMemo(() => ({
        ...dashboardActionButtonSx,
        color: canAnalyzeSelection ? '#2196f3' : '#9e9e9e',
    }), [canAnalyzeSelection, dashboardActionButtonSx]);

    const handleDelete = useCallback((ev, fileId) => {
        ev.stopPropagation();
        const fileToDelete = files.find((file) => file.id === fileId);

        deleteFileModal.openModal({
            fileId,
            fileName: fileToDelete?.info?.name,
            onSuccess: async () => {
                await getAllFiles();
            }
        });
    }, [deleteFileModal, files, getAllFiles]);

    const handleChangePatient = useCallback((ev, fileId, fileName, currentPatientId = null) => {
        ev.stopPropagation();
        moveFileToPatientModal.openModal({
            fileId,
            patientId: currentPatientId,
            fileName,
            onSuccess: getAllFiles
        });
    }, [getAllFiles, moveFileToPatientModal]);

    const webColumns = useMemo(() => [
        {
            field: 'info',
            headerName: 'Nome File Trascritto',
            flex: 1,
            renderCell: (({ value }) => {
                return <Stack height={'100%'} spacing={0} direction={'column'} justifyContent={'center'} alignItems={'start'}>
                    {value.patientId && <NavLink
                        to={`/patient/${value.patientId}`}
                        onClick={(ev) => ev.stopPropagation()}
                    >
                        <Stack direction={'row'} alignItems={'center'} spacing={1} color={'black'} sx={{
                            opacity: .7,
                            transition: 'all ease-out .3s',
                            '&:hover': { opacity: 1 }
                        }}>
                            <FolderSharedOutlinedIcon fontSize="small" />
                            <Typography variant='body2' fontSize={".8rem"}>{value.patientName}</Typography>
                        </Stack>
                    </NavLink>}
                    <Tooltip title="Fai doppio clic per visualizzare la Trascrizione" placement="top-start">
                        <Typography variant='body1' sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }}>{value.name}</Typography>
                    </Tooltip>
                </Stack>
            })
        },
        {
            field: 'uploadDate',
            headerName: 'Data',
            renderCell: ({ value }) => {
                return <Stack height={'100%'} spacing={0} alignItems={'center'} justifyContent={'center'}>
                    <Typography variant='body2'>{moment(value).format('DD/MM/YY')}</Typography>
                    <Typography variant='body2'>{moment(value).format('HH:mm:ss')}</Typography>
                </Stack>
            }
        },
        {
            field: 'duration',
            headerName: 'Durata',
            width: 80,
            valueFormatter: (duration) => {
                if (!duration) return "-";
                const totalSeconds = parseInt(duration);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                return `${minutes !== 0 ? minutes + "m " : ""}${seconds}s`;
            }
        },
        {
            field: 'state',
            headerName: 'Stato',
            width: 60,
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ value }) => {
                return renderFileStatus(value);
            }
        },
        {
            field: 'actions',
            headerName: 'Azioni',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => {
                return <ButtonGroup variant="text">
                    <IconButton color="error" aria-label="delete" onClick={(ev) => handleDelete(ev, row.id)}>
                        <DeleteIcon />
                    </IconButton>
                    <IconButton color="info" aria-label="upload" onClick={(ev) => handleChangePatient(ev, row.id, row.info.name, row.info.patientId)}>
                        <DriveFileMoveOutlinedIcon />
                    </IconButton>
                </ButtonGroup>
            }
        }
    ], [handleChangePatient, handleDelete]);

    const mobileColumns = useMemo(() => [
        {
            field: 'state',
            headerName: '',
            width: 30,
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ value }) => {
                return renderFileStatus(value);
            }
        },
        {
            field: 'info',
            headerName: 'Nome File Trascritto',
            width: 200,
            renderCell: ({ value }) => {
                return <Stack height={'100%'} spacing={0} direction={'column'} justifyContent={'center'} alignItems={'start'}>
                    {value.patientId && <NavLink
                        to={`/patient/${value.patientId}`}
                        onClick={(ev) => ev.stopPropagation()}
                    >
                        <Stack direction={'row'} alignItems={'center'} spacing={1} color={'black'} sx={{
                            opacity: .7,
                            transition: 'all ease-out .3s',
                            '&:hover': { opacity: 1 }
                        }}>
                            <FolderSharedOutlinedIcon fontSize="small" />
                            <Typography variant='body2' fontSize={".8rem"}>{value.patientName}</Typography>
                        </Stack>
                    </NavLink>}
                    <Tooltip title="Fai doppio clic per visualizzare la Trascrizione" placement="top-start">
                        <Typography variant='body1' sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }}>{value.name}</Typography>
                    </Tooltip>
                </Stack>
            }
        },
        // {
        //     field: 'duration',
        //     headerName: 'Durata',
        //     width: 100,
        //     valueFormatter: (duration) => {
        //         if (!duration) return "-";
        //         const totalSeconds = parseInt(duration);
        //         const minutes = Math.floor(totalSeconds / 60);
        //         const seconds = totalSeconds % 60;
        //         return `${minutes !== 0 ? minutes + "m " : ""}${seconds}s`;
        //     }
        // },
        // {
        //     field: 'uploadDate',
        //     headerName: 'Data',
        //     valueFormatter: (date) => {
        //         return moment(date).format('D MMMM YYYY, HH:mm:ss');
        //     }
        // },
        {
            field: 'actions',
            headerName: 'Azioni',
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => {
                return <ButtonGroup variant="text">
                    <IconButton color="error" aria-label="delete" onClick={(ev) => handleDelete(ev, row.id)}>
                        <DeleteIcon />
                    </IconButton>
                    <IconButton color="info" aria-label="move-to-patient" onClick={(ev) => handleChangePatient(ev, row.id, row.info.name, row.info.patientId)}>
                        <DriveFileMoveOutlinedIcon />
                    </IconButton>
                </ButtonGroup>
            }
        }
    ], [handleChangePatient, handleDelete]);

    useEffect(() => {
        getAllFiles();
    }, [getAllFiles]);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        getAllFiles();
        pollingIntervalRef.current = setInterval(() => {
            getAllFiles();
        }, 3000);
    }, [getAllFiles, stopPolling]);

    useEffect(() => {
        const hasWaitingFiles = files.some(file => file.state === 'waiting');

        if (hasWaitingFiles && !previousHasWaitingRef.current) {
            startPolling();
        } else if (!hasWaitingFiles && previousHasWaitingRef.current) {
            stopPolling();
        }

        previousHasWaitingRef.current = hasWaitingFiles;
    }, [files, startPolling, stopPolling]);

    useEffect(() => {
        if (!isDocumentVisible && previousHasWaitingRef.current) {
            backgroundedDuringPollingRef.current = true;
        }
    }, [isDocumentVisible]);

    useEffect(() => {
        files.forEach((currentFile) => {
            const previousFile = previousFilesRef.current.find((file) => file.id === currentFile.id);

            if (previousFile?.state === 'waiting' && currentFile.state === 'done') {
                if (!isDocumentVisible || !backgroundedDuringPollingRef.current) {
                    notifyTranscriptionCompleted({
                        fileId: currentFile.id,
                        fileName: currentFile.info?.name
                    });
                }
            }
        });

        previousFilesRef.current = files;
        if (files.every((file) => file.state !== 'waiting')) {
            backgroundedDuringPollingRef.current = false;
        }
    }, [files, isDocumentVisible, notifyTranscriptionCompleted]);

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!selectedFileIds.length) {
            return;
        }

        const stillExists = selectedFileIds.some((selectedId) => files.some((file) => file.id === selectedId));
        if (!stillExists) {
            setSelectedFileIds([]);
        }
    }, [files, selectedFileIds]);

    const onFileUpload = useCallback(() => {
        getAllFiles();
    }, [getAllFiles]);

    useEffect(() => {
        if (isAdminVerificationPending) {
            if (pendingRootFileIdsKey) {
                setPendingRootFileIdsKey('');
            }
            return;
        }

        if (isPatientView || response === null || !Array.isArray(files)) {
            return;
        }

        const rootFiles = files.filter((file) => !file.info?.patientId);
        const rootFileIdsKey = rootFiles.map((file) => file.id).sort((a, b) => a - b).join(',');

        if (rootFiles.length === 0) {
            if (pendingRootFileIdsKey) {
                setPendingRootFileIdsKey('');
            }
            return;
        }

        if (rootFileIdsKey === pendingRootFileIdsKey) {
            return;
        }

        setPendingRootFileIdsKey(rootFileIdsKey);
        moveFileToPatientModal.openModal({
            fileIds: rootFiles.map((file) => file.id),
            title: 'Sposta i file',
            showInlineCreatePatientAction: true,
            helperText: 'Operazione obbligatoria.',
            confirmLabel: 'Conferma',
            requireSelection: true,
            preventClose: true,
            closeWarningMessage: "Per analizzare le sedute e monitorare i percorsi dei pazienti devi prima assegnare questi file a un paziente.",
            errorMessage: 'Impossibile spostare i file nel paziente selezionato. Riprova.',
            onSuccess: async () => {
                await getAllFiles();
            }
        });
    }, [files, getAllFiles, isAdminVerificationPending, isPatientView, moveFileToPatientModal, pendingRootFileIdsKey, response]);

    const closeUploadDialog = useCallback(() => {
        setUploadDialogOpen(false);
        setAutoOpenUploadPicker(false);
        setPrefilledUploadFile(null);
        setActiveActionPatient(null);
        setRetryActionPatient(null);
    }, []);

    const closeRecorderDialog = useCallback(() => {
        setRecorderDialogOpen(false);
        setActiveActionPatient(null);
        setRetryActionPatient(null);
    }, []);

    const closeUploadDialogForTranscription = useCallback(() => {
        setRetryActionPatient(activeActionPatient);
        setUploadDialogOpen(false);
        setAutoOpenUploadPicker(false);
        setPrefilledUploadFile(null);
        setActiveActionPatient(null);
    }, [activeActionPatient]);

    const closeRecorderDialogForTranscription = useCallback(() => {
        setRetryActionPatient(activeActionPatient);
        setRecorderDialogOpen(false);
        setActiveActionPatient(null);
    }, [activeActionPatient]);

    const retryUploadAfterTranscriptionError = useCallback(() => {
        setUploadDialogOpen(true);
        setAutoOpenUploadPicker(true);
    }, []);

    const retryRecordAfterTranscriptionError = useCallback(() => {
        setRecorderDialogOpen(true);
    }, []);

    const openActionSelectionModal = useCallback((actionType) => {
        if (targetPatientId) {
            if (actionType === ACTION_TYPES.upload) {
                setAutoOpenUploadPicker(false);
                window.setTimeout(() => {
                    directUploadInputRef.current?.click();
                }, 0);
                return;
            }

            setRecorderDialogOpen(true);
            return;
        }
        
        moveFileToPatientModal.openModal({
            selectOnly: true,
            patientId: activeActionPatient?.id ?? null,
            patientName: activeActionPatient?.name ?? null,
            title: 'Seleziona Paziente',
            selectionLabel: '',
            description: <>Seleziona una cartella paziente o creane una nuova.</>,
            helperText: '',
            confirmLabel: actionType === ACTION_TYPES.upload ? 'Seleziona e carica' : 'Seleziona e registra',
            requireSelection: true,
            onSuccess: (selectedPatient) => {
                setActiveActionPatient(selectedPatient);

                if (actionType === ACTION_TYPES.upload) {
                    setAutoOpenUploadPicker(false);
                    window.setTimeout(() => {
                        directUploadInputRef.current?.click();
                    }, 0);
                    return;
                }

                setRecorderDialogOpen(true);
            }
        });
    }, [activeActionPatient?.id, activeActionPatient?.name, moveFileToPatientModal, targetPatientId]);

    const uploadFileBtnClickHandler = () => {
        openActionSelectionModal(ACTION_TYPES.upload);
    }

    const directUploadFileChangeHandler = (event) => {
        const selectedFile = event.target.files?.[0];

        if (!selectedFile) {
            return;
        }

        setPrefilledUploadFile(selectedFile);
        setUploadDialogOpen(true);
        event.target.value = null;
    };

    const recordBtnClickHandler = () => {
        openActionSelectionModal(ACTION_TYPES.record);
    }

    const patientOptionsOpen = Boolean(patientOptionsAnchorEl);

    const openPatientOptionsHandler = (event) => {
        setPatientOptionsAnchorEl(event.currentTarget);
    };

    const closePatientOptionsHandler = () => {
        setPatientOptionsAnchorEl(null);
    };

    const renamePatientClickHandler = () => {
        closePatientOptionsHandler();
        renamePatientModal.openModal({
            patientId,
            patientName: currentPatientName,
        });
    };

    const deletePatientClickHandler = () => {
        closePatientOptionsHandler();
        deletePatientModal.openModal({
            patientId,
            patientName: currentPatientName,
            onSuccess: () => navigate('/dashboard')
        });
    };

    const rowClickHandler = (params) => {
        setSelectedFileIds((currentValue) => (
            currentValue[0] === params.id ? [] : [params.id]
        ));
    };

    const rowDoubleClickHandler = (params) => {
        navigate(`/files/${params.id}`);
    };

    const patientAssistantClickHandler = useCallback(() => {
        if (!canAnalyzeSelection) {
            setEmptyAssistantDialogOpen(true);
            return;
        }

        if (isPatientView) {
            navigate({
                pathname: `/patient/${patientId}/assistant`,
                search: selectedFile ? createSearchParams({ fileId: String(selectedFile.id) }).toString() : ''
            });
            return;
        }

        navigate({
            pathname: `/patient/${selectedFile.info.patientId}/assistant`,
            search: createSearchParams({ fileId: String(selectedFile.id) }).toString()
        });
    }, [canAnalyzeSelection, isPatientView, navigate, patientId, selectedFile]);

    const noRowsOverlay = () => (
        <Stack height="100%" alignItems="center" justifyContent="center">
            <Typography variant="body2" sx={{ opacity: 0.65, fontSize: '0.9rem' }}>
                {isPatientView ? 'Ancora nessun file per questo paziente' : 'Nessun file disponibile'}
            </Typography>
        </Stack>
    );

    return <>
        <VisuallyHiddenInput
            ref={directUploadInputRef}
            type="file"
            accept="audio/mp3,audio/*"
            onChange={directUploadFileChangeHandler}
        />
        <FileUploadDialog currentPatientId={targetPatientId} currentPatientName={targetPatientName} autoOpenFilePicker={autoOpenUploadPicker} preselectedFile={prefilledUploadFile} open={uploadDialogOpen} onFileUpload={onFileUpload} onClose={closeUploadDialog} onRetryAfterError={retryUploadAfterTranscriptionError} onTranscriptionStarted={closeUploadDialogForTranscription}></FileUploadDialog>
        <AudioRecorderDialog currentPatientId={targetPatientId} currentPatientName={targetPatientName} open={recorderDialogOpen} onFileUpload={onFileUpload} onClose={closeRecorderDialog} onRetryAfterError={retryRecordAfterTranscriptionError} onTranscriptionStarted={closeRecorderDialogForTranscription}></AudioRecorderDialog>
        <ResponsiveDialog
            isOpen={emptyAssistantDialogOpen}
            onClose={() => setEmptyAssistantDialogOpen(false)}
            title=" "
            showCloseButton
            minWidth="360px"
            minHeight="auto"
            actions={(
                <Button onClick={() => setEmptyAssistantDialogOpen(false)}>
                    Chiudi
                </Button>
            )}
        >
            <Typography
                textAlign="center"
                sx={{
                    fontSize: { xs: '1rem', sm: '1.05rem' },
                    lineHeight: 1.5,
                    maxWidth: '28ch',
                    py: 1
                }}
            >
                {isPatientView
                    ? "Carica e trascrivi un file prima di fare l'analisi."
                    : "Crea o Seleziona la cartella/file paziente per l'analisi AI."}
            </Typography>
        </ResponsiveDialog>

        <Stack spacing={2} mb={2} flexWrap={'wrap'} alignItems="center" justifyContent={'center'}>
            <Stack
                width={'100%'}
                spacing={2}
                direction={{ sm: 'row' }}
                flexWrap={'wrap'}
                alignItems="center"
                justifyContent={'center'}
                sx={{ position: 'relative' }}
            >
                {((patientId && scope !== 'root') || (!patientId || scope === 'root')) && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minWidth: 0, mx: 'auto', width: { xs: '100%', sm: 'calc(100% - 72px)' }, px: { xs: 1, sm: 3 } }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            sx={{
                                maxWidth: { xs: '100%', sm: '640px' },
                                whiteSpace: 'normal',
                                overflow: 'visible',
                                textOverflow: 'clip',
                                fontSize: { xs: '1.1rem', sm: '1.3rem' },
                                color: '#000',
                                textAlign: 'center',
                                lineHeight: 1.2
                            }}
                        >
                            {(patientId && scope !== 'root') ? currentPatientName : title}
                        </Typography>
                    </Stack>
                )}
                {(patientId && scope !== 'root') && <Stack direction={'row'} justifyContent={'end'} sx={{ position: { xs: 'static', sm: 'absolute' }, right: 0 }} >
                    <IconButton
                        onClick={openPatientOptionsHandler}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={patientOptionsAnchorEl}
                        open={patientOptionsOpen}
                        onClose={closePatientOptionsHandler}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={renamePatientClickHandler}>Rinomina Paziente</MenuItem>
                        <MenuItem onClick={deletePatientClickHandler}>Elimina Paziente</MenuItem>
                    </Menu>
                </Stack>}
            </Stack>
            <Stack
                width={'100%'}
                spacing={2}
                gap={{ xs: 1, sm: 0 }}
                direction={{ xs: 'column', sm: 'row' }}
                flexWrap={'wrap'}
                alignItems={{ xs: 'stretch', sm: 'center' }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} gap={{ xs: 1, sm: 0 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button
                        variant="text"
                        sx={dashboardActionButtonSx}
                        onClick={uploadFileBtnClickHandler}
                        startIcon={<UploadFileIcon />}>
                        Carica file
                    </Button>
                    <Button
                        variant="text"
                        sx={dashboardActionButtonSx}
                        onClick={recordBtnClickHandler}
                        startIcon={<MicIcon />}>
                        Registra
                    </Button>
                </Stack>
                <Tooltip title="Analizza con l'AI">
                    <Button
                        sx={{
                            ml: { xs: '0!important', sm: 'auto!important' },
                            ...assistantButtonSx
                        }}
                        variant="text"
                        onClick={patientAssistantClickHandler}
                        startIcon={<AutoAwesomeOutlinedIcon />}>
                        Analizza AI
                    </Button>
                </Tooltip>
            </Stack>
        </Stack>
        <Box mb={3}>
            <DataGrid
                onRowClick={rowClickHandler}
                onRowDoubleClick={rowDoubleClickHandler}
                rows={files}
                getRowHeight={() => 72}
                columns={isAppView ? mobileColumns : webColumns}
                rowSelectionModel={selectedFileIds}
                sx={{
                    '& .MuiDataGrid-row': {
                        cursor: 'pointer'
                    },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none'
                    },
                    '& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within': {
                        outline: 'none'
                    }
                }}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 10
                        }
                    }
                }}
                pageSizeOptions={[5, 10, 15]}
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                slots={{
                    noRowsOverlay
                }}
            />
        </Box>
    </>
}

export default FileList;
